import React, { useState, useMemo } from "react";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import { useEscapeKey } from "../ui/useEscapeKey";
import { formatWeightFromGrams } from "../../utils/weightUtils";
import ColorDot from "../ColorDot/ColorDot";
import "./CreatePreRollModal.css";

const SIZE_PRESETS = [0.5, 0.75, 1, 1.5, 2];

export default function CreatePreRollModal({ sourceProduct, licenseNo, locationId, onClose, onSuccess }) {
    const toast = useToast();
    useEscapeKey(onClose);

    const availableGrams = parseFloat(sourceProduct?.quantity) || 0;
    const sourceColor = (sourceProduct?.color || "").toLowerCase();

    const [unitSize, setUnitSize] = useState(1);
    const [customSize, setCustomSize] = useState("");
    const [useCustom, setUseCustom] = useState(false);
    const [unitCount, setUnitCount] = useState("");
    const [productName, setProductName] = useState(`${sourceProduct?.product_name || ""} Pre-Roll`);
    const [price, setPrice] = useState("");
    const [sku, setSku] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const effectiveSize = useCustom ? parseFloat(customSize) || 0 : unitSize;
    const gramsNeeded = effectiveSize * (parseInt(unitCount) || 0);
    const maxUnits = effectiveSize > 0 ? Math.floor(availableGrams / effectiveSize) : 0;

    const canSubmit = useMemo(() => {
        return (
            effectiveSize > 0 &&
            parseInt(unitCount) > 0 &&
            gramsNeeded <= availableGrams &&
            productName.trim().length > 0 &&
            !submitting
        );
    }, [effectiveSize, unitCount, gramsNeeded, availableGrams, productName, submitting]);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await apiClient.post("/prerolls", {
                sourceProductId: sourceProduct.id,
                licenseNo,
                locationId: locationId || null,
                productName: productName.trim(),
                unitSize: effectiveSize,
                unitCount: parseInt(unitCount),
                price: price ? parseFloat(price) : null,
                sku: sku.trim() || null,
                description: description.trim() || null,
            });
            toast.success(`Created ${unitCount} pre-rolls from ${sourceProduct.product_name}`);
            onSuccess?.();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.error || "Failed to create pre-roll";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="cpm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="prm" onClick={(e) => e.stopPropagation()}>
                <div className="prm__header">
                    <h3 className="prm__title">Create Pre-Roll</h3>
                    <button className="cpm__close" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="prm__body">
                    <div className="prm__source">
                        <ColorDot color={sourceColor} className="prm__source-dot" />
                        <div className="prm__source-info">
                            <span className="prm__source-name">{sourceProduct?.product_name}</span>
                            <span className="prm__source-avail">
                                Available: {formatWeightFromGrams(availableGrams)}
                            </span>
                        </div>
                    </div>

                    <div className="prm__field">
                        <label className="prm__label">Unit Size (grams per pre-roll)</label>
                        <div className="prm__size-presets">
                            {SIZE_PRESETS.map((s) => (
                                <button
                                    key={s}
                                    className={`prm__size-btn${!useCustom && unitSize === s ? " prm__size-btn--active" : ""}`}
                                    onClick={() => { setUseCustom(false); setUnitSize(s); }}
                                >
                                    {s}g
                                </button>
                            ))}
                            <button
                                className={`prm__size-btn${useCustom ? " prm__size-btn--active" : ""}`}
                                onClick={() => setUseCustom(true)}
                            >
                                Custom
                            </button>
                        </div>
                        {useCustom && (
                            <input
                                type="number"
                                className="prm__input"
                                placeholder="Enter size in grams"
                                value={customSize}
                                onChange={(e) => setCustomSize(e.target.value)}
                                min="0.1"
                                step="0.1"
                                autoFocus
                            />
                        )}
                    </div>

                    <div className="prm__field">
                        <label className="prm__label">
                            Number of Pre-Rolls
                            {effectiveSize > 0 && (
                                <span className="prm__label-hint"> (max {maxUnits})</span>
                            )}
                        </label>
                        <input
                            type="number"
                            className="prm__input"
                            placeholder={`Up to ${maxUnits}`}
                            value={unitCount}
                            onChange={(e) => setUnitCount(e.target.value)}
                            min="1"
                            max={maxUnits}
                        />
                        {gramsNeeded > 0 && (
                            <span className={`prm__gram-calc${gramsNeeded > availableGrams ? " prm__gram-calc--over" : ""}`}>
                                = {gramsNeeded}g of flower
                                {gramsNeeded > availableGrams && " (exceeds available)"}
                            </span>
                        )}
                    </div>

                    <div className="prm__field">
                        <label className="prm__label">Product Name</label>
                        <input
                            type="text"
                            className="prm__input"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </div>

                    <div className="prm__row">
                        <div className="prm__field prm__field--half">
                            <label className="prm__label">Price per Unit</label>
                            <input
                                type="number"
                                className="prm__input"
                                placeholder="Optional"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="prm__field prm__field--half">
                            <label className="prm__label">SKU</label>
                            <input
                                type="text"
                                className="prm__input"
                                placeholder="Optional"
                                value={sku}
                                onChange={(e) => setSku(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="prm__field">
                        <label className="prm__label">Description</label>
                        <textarea
                            className="prm__textarea"
                            placeholder="Optional"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                        />
                    </div>
                </div>

                <div className="prm__footer">
                    <button className="prm__btn prm__btn--cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="prm__btn prm__btn--submit"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {submitting ? "Creating..." : `Create ${unitCount || 0} Pre-Rolls`}
                    </button>
                </div>
            </div>
        </div>
    );
}
